import axios from 'axios'
import { loadChargebeeJs } from './useChargebeeCheckout'

// The one way into the Chargebee self-serve portal, shared by every entry point.
//
// The session's mint site and the site Chargebee.js is initialised with must
// match, or Chargebee rejects the session as "Token has expired". The server
// mints against config.billing.site and reports it as billing.config.site, so
// that — not the app-variant site — is the `site` to pass here. Handing over a
// session also spares an already-signed-in user a one-time email code.
//
// `fallbackUrl` is the hosted portal, tried only if the session or Chargebee.js
// won't load; a "portal access via API only" site refuses it, so it is last.

// Our app sets a dark color-scheme on <html>, under which Chromium paints an
// opaque white backdrop behind the cross-origin portal iframe, covering the
// page. Forcing color-scheme: light on Chargebee's container removes it.
function ensurePortalStyles() {
    if (typeof document === 'undefined') return
    if (document.getElementById('cb-portal-color-scheme')) return
    const style = document.createElement('style')
    style.id = 'cb-portal-color-scheme'
    style.textContent =
        '#cb-container, #cb-container iframe { color-scheme: light; }'
    document.head.appendChild(style)
}

export async function openBillingPortal({ site, fallbackUrl, onClose } = {}) {
    ensurePortalStyles()
    let portalSession = null
    try {
        const response = await axios.post('/billing/portal-session', {
            redirectUrl:
                typeof window !== 'undefined' ? window.location.href : undefined,
        })
        portalSession = response.data
    } catch (error) {
        console.warn(
            '[billing] no portal session, so Chargebee will ask for a code by' +
                ' email. If this persists, in the Chargebee site settings set' +
                ' "Customers can access the self-serve portal" to "via single' +
                ' sign-on API":',
            error && error.message
        )
    }

    try {
        const Chargebee = await loadChargebeeJs()
        const instance = Chargebee.init(site ? { site } : {})
        if (portalSession) {
            instance.setPortalSession(() => Promise.resolve(portalSession))
        }
        instance
            .createChargebeePortal()
            .open(onClose ? { close: onClose } : undefined)
        return true
    } catch (error) {
        console.warn(
            '[billing] portal could not be opened:',
            error && error.message
        )
        if (fallbackUrl && typeof window !== 'undefined') {
            window.open(fallbackUrl, '_blank', 'noreferrer')
        }
        return false
    }
}
